<template><div><component-combinator component-name="DtCard" /><h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Cards are surfaces that display content and actions on a single topic.
They should be easy to scan for relevant and actionable information. Elements, like text and images, should be placed on them in a way that clearly indicates hierarchy.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">To display content and actions on a single topic.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Add too many call-to-action elements to the same card. A card should only contain a single primary action.</li>
<li class="d-docsite--list-element">Inform users about important changes.</li>
</ul>
</template>
</dialtone-usage>
<h3 id="best-practices" tabindex="-1" class="d-docsite--header-3">Best Practices <a class="header-anchor d-link d-docsite--link d-link" href="#best-practices" aria-hidden="true">#</a></h3>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">It should only contain a single idea that may feature a call-to-action, or the option to navigate to more detailed content.</li>
<li class="d-docsite--list-element">The content of a card should be concise and offer only a preview of detailed content.</li>
<li class="d-docsite--list-element">The headings should set clear expectations about the card's purpose.</li>
</ul>
<h2 id="variants-and-examples" tabindex="-1" class="d-docsite--header-2">Variants and Examples <a class="header-anchor d-link d-docsite--link d-link" href="#variants-and-examples" aria-hidden="true">#</a></h2>
<h3 id="base" tabindex="-1" class="d-docsite--header-3">Base <a class="header-anchor d-link d-docsite--link d-link" href="#base" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-card class=&quot;d-w-400&quot; header-class=&quot;h:d-bgc-moderate-opaque&quot; content-class=&quot;h:d-bgc-moderate-opaque&quot; footer-class=&quot;h:d-bgc-moderate-opaque&quot;&gt;
  &lt;template #header&gt;
    (header slot)
  &lt;/template&gt;
  &lt;template #content&gt;
    (content slot)
  &lt;/template&gt;
  &lt;template #footer&gt;
    (footer slot)
  &lt;/template&gt;
&lt;/dt-card&gt;'>
<dt-card class="d-w-400" header-class="h:d-bgc-moderate-opaque" content-class="h:d-bgc-moderate-opaque" footer-class="h:d-bgc-moderate-opaque">
  <template #header>
    (header slot)
  </template>
  <template #content>
    (content slot)
  </template>
  <template #footer>
    (footer slot)
  </template>
</dt-card>
</code-example>
<h3 id="with-header" tabindex="-1" class="d-docsite--header-3">With Header <a class="header-anchor d-link d-docsite--link d-link" href="#with-header" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-card class=&quot;d-w-400&quot;&gt;
  &lt;template #header&gt;
    &lt;dt-text as=&quot;p&quot; kind=&quot;headline&quot; :size=&quot;300&quot;&gt;Lorem ipsum&lt;/dt-text&gt;
    &lt;dt-button
      :size=&quot;100&quot;
      importance=&quot;clear&quot;
      kind=&quot;muted&quot;
      aria-label=&quot;Menu button&quot;
    &gt;
      &lt;template #startIcon&gt;
        &lt;dt-icon
          name=&quot;more-vertical&quot;
          size=&quot;100&quot;
        /&gt;
      &lt;/template&gt;
    &lt;/dt-button&gt;
  &lt;/template&gt;
  &lt;template #content&gt;
    Content slot. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec fermentum molestie semper. Morbi finibus nulla turpis, nec molestie mi rutrum.
  &lt;/template&gt;
&lt;/dt-card&gt;'>
<dt-card class="d-w-400">
  <template #header>
    <dt-text as="p" kind="headline" :size="300">Lorem ipsum</dt-text>
    <dt-button
      :size="100"
      importance="clear"
      kind="muted"
      aria-label="Menu button"
    >
      <template #startIcon>
        <dt-icon
          name="more-vertical"
          size="100"
        />
      </template>
    </dt-button>
  </template>
  <template #content>
    Content slot. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec fermentum molestie semper. Morbi finibus nulla turpis, nec molestie mi rutrum.
  </template>
</dt-card>
</code-example>
<h3 id="with-footer" tabindex="-1" class="d-docsite--header-3">With Footer <a class="header-anchor d-link d-docsite--link d-link" href="#with-footer" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-card class=&quot;d-w-400&quot;&gt;
  &lt;template #content&gt;
    Content slot. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec fermentum molestie semper. Morbi finibus nulla turpis, nec molestie mi rutrum.
  &lt;/template&gt;
  &lt;template #footer&gt;
    &lt;dt-button
      importance=&quot;outlined&quot;
      :size=&quot;200&quot;
    &gt;
      Button
    &lt;/dt-button&gt;
  &lt;/template&gt;
&lt;/dt-card&gt;'>
<dt-card class="d-w-400">
  <template #content>
    Content slot. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec fermentum molestie semper. Morbi finibus nulla turpis, nec molestie mi rutrum.
  </template>
  <template #footer>
    <dt-button
      importance="outlined"
      :size="200"
    >
      Button
    </dt-button>
  </template>
</dt-card>
</code-example>
<h3 id="content-only" tabindex="-1" class="d-docsite--header-3">Content Only <a class="header-anchor d-link d-docsite--link d-link" href="#content-only" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-card class=&quot;d-w-400&quot;&gt;
  &lt;template #content&gt;
    Content slot. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec fermentum molestie semper. Morbi finibus nulla turpis, nec molestie mi rutrum.
  &lt;/template&gt;
&lt;/dt-card&gt;'>
<dt-card class="d-w-400">
  <template #content>
    Content slot. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec fermentum molestie semper. Morbi finibus nulla turpis, nec molestie mi rutrum.
  </template>
</dt-card>
</code-example>
<h3 id="with-header-footer-and-scrollable-content" tabindex="-1" class="d-docsite--header-3">With Header, Footer and Scrollable Content <a class="header-anchor d-link d-docsite--link d-link" href="#with-header-footer-and-scrollable-content" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-card class=&quot;d-w-400&quot; content-class=&quot;d-pie-0&quot;&gt;
  &lt;template #header&gt;
    &lt;dt-text as=&quot;p&quot; kind=&quot;headline&quot; :size=&quot;300&quot;&gt;Lorem ipsum&lt;/dt-text&gt;
    &lt;dt-button
      :size=&quot;100&quot;
      importance=&quot;clear&quot;
      kind=&quot;muted&quot;
      aria-label=&quot;Menu button&quot;
    &gt;
      &lt;template #startIcon&gt;
        &lt;dt-icon
          name=&quot;more-vertical&quot;
          size=&quot;100&quot;
        /&gt;
      &lt;/template&gt;
    &lt;/dt-button&gt;
  &lt;/template&gt;
  &lt;template #content&gt;
    &lt;div class=&quot;d-h-125 d-pie-200&quot; v-dt-scrollbar:never&gt;Content slot. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec fermentum molestie semper. Morbi finibus nulla turpis, nec molestie mi rutrum.&lt;/div&gt;
  &lt;/template&gt;
  &lt;template #footer&gt;
    &lt;dt-button
      importance=&quot;outlined&quot;
      :size=&quot;200&quot;
    &gt;
      Button
    &lt;/dt-button&gt;
  &lt;/template&gt;
&lt;/dt-card&gt;'>
<dt-card class="d-w-400" content-class="d-pie-0">
  <template #header>
    <dt-text as="p" kind="headline" :size="300">Lorem ipsum</dt-text>
    <dt-button
      :size="100"
      importance="clear"
      kind="muted"
      aria-label="Menu button"
    >
      <template #startIcon>
        <dt-icon
          name="more-vertical"
          size="100"
        />
      </template>
    </dt-button>
  </template>
  <template #content>
    <div class="d-h-125 d-pie-200" v-dt-scrollbar:never>Content slot. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec fermentum molestie semper. Morbi finibus nulla turpis, nec molestie mi rutrum.</div>
  </template>
  <template #footer>
    <dt-button
      importance="outlined"
      :size="200"
    >
      Button
    </dt-button>
  </template>
</dt-card>
</code-example>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="card" /><h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">At minimum, card contains body of content. It could also have header with buttons, and footer with buttons/text.</p>
<component-class-table component-name="card"></component-class-table></div></template>


